import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../store';
import { 
  Card, 
  Button, 
  Modal, 
  Badge, 
  ConfirmDialog,
  NotificationModal
} from '@tadagram/ui';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Mail,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  Music2 as TikTokIcon,
  MessageCircle as ZaloIcon,
  Send as TelegramIcon,
  AtSign as ThreadsIcon,
  Linkedin as LinkedinIcon,
  Clock,
  User,
  Eye,
  ClipboardPaste,
  QrCode,
  RefreshCw,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { SeedingAccountForm, DemographicsPanel } from '../components';
import { SeedingAccount } from '../types';
import { apiService } from '../services/api';
import { getAccountBadge } from '../utils/accountBadge';
import { FanpageManagement } from './FanpageManagement';

const PLATFORM_ICONS: Record<string, React.ReactNode> = {
  facebook: <FacebookIcon size={20} />,
  instagram: <InstagramIcon size={20} />,
  threads: <ThreadsIcon size={20} />,
  twitter: <TwitterIcon size={20} />,
  tiktok: <TikTokIcon size={20} />,
  zalo: <ZaloIcon size={20} />,
  telegram: <TelegramIcon size={20} />,
  linkedin: <LinkedinIcon size={20} />,
  reddit: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <circle cx="8.5" cy="10.5" r="1.5"/>
      <circle cx="15.5" cy="10.5" r="1.5"/>
      <path d="M8 15c1 1 3 1.5 4 1.5s3-.5 4-1.5"/>
    </svg>
  ),
};

const PLATFORM_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  threads: 'Threads',
  twitter: 'X (Twitter)',
  tiktok: 'TikTok',
  zalo: 'Zalo',
  telegram: 'Telegram',
  reddit: 'Reddit',
  linkedin: 'LinkedIn',
  all: 'All',
};

interface AccountManagementProps {
  activePlatform?: string; // From App.tsx platform sidebar
  activeWorkerId?: string | null; // From App.tsx worker sidebar
  onDataChange?: () => void; // Callback to refresh data in parent
  workerStatusMap?: Record<string, string>; // { [worker_uuid]: 'online' | 'offline' | 'expired' }
}

export const AccountManagement: React.FC<AccountManagementProps> = ({ 
  activePlatform = 'facebook',
  activeWorkerId = null,
  onDataChange,
  workerStatusMap = {},
}) => {
  const { t } = useTranslation('account');
  const { 
    seedingAccounts, 
    loadSeedingAccounts,
    deleteSeedingAccount
  } = useAppStore();

  const [selectedAccount, setSelectedAccount] = useState<SeedingAccount | null>(null);
  // Facebook sub-tab: 'personal' (seeding accounts) | 'fanpage'
  const [fbTab, setFbTab] = useState<'personal' | 'fanpage'>('personal');
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDemographicsModalOpen, setIsDemographicsModalOpen] = useState(false);
  const [viewingAccount, setViewingAccount] = useState<SeedingAccount | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  // Quick-fill state
  const [showQuickFill, setShowQuickFill] = useState(false);
  const [quickFillInput, setQuickFillInput] = useState('');
  const [quickFillData, setQuickFillData] = useState<Record<string, string> | undefined>(undefined);

  const QUICK_FILL_FIELDS = ['username', 'password', 'two_fa_secret', 'email', 'phone', 'recovery_email', 'fullname'] as const;

  const handleQuickFillConfirm = () => {
    const parts = quickFillInput.split('|').map(s => s.trim());
    const data: Record<string, string> = {};
    QUICK_FILL_FIELDS.forEach((field, i) => {
      if (i < parts.length && parts[i]) {
        data[field] = parts[i];
      }
    });
    setQuickFillData(data);
    setShowQuickFill(false);
    setQuickFillInput('');
  };
  
  // Notification state
  const [notification, setNotification] = useState<{
    show: boolean;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
  }>({ show: false, type: 'info', title: '', message: '' });

  // TikTok QR login modal state
  const [qrModal, setQrModal] = useState<{
    open: boolean;
    account: SeedingAccount | null;
    qrBase64: string | null;
    status: 'loading' | 'ready' | 'success' | 'error';
    expiresAt: number | null;
  }>({ open: false, account: null, qrBase64: null, status: 'loading', expiresAt: null });
  const qrPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const qrAccountPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Cached { url, jwt } for the worker serving the current QR account
  const workerSessionRef = useRef<{ url: string; jwt: string } | null>(null);

  useEffect(() => {
    loadSeedingAccounts();
  }, [loadSeedingAccounts]);

  // Filter accounts by platform AND worker
  const filteredAccounts = useMemo(() => {
    let filtered = seedingAccounts;
    
    // Filter by platform
    if (activePlatform && activePlatform !== 'all') {
      filtered = filtered.filter(account => 
        account.platform?.toLowerCase() === activePlatform.toLowerCase()
      );
    }
    
    // Filter by active worker if selected
    if (activeWorkerId) {
      filtered = filtered.filter(account => account.worker_uuid === activeWorkerId);
    }
    
    return filtered;
  }, [seedingAccounts, activePlatform, activeWorkerId]);

  // Worker stats removed - now handled by WorkerListSidebar

  const handleSyncVault = async () => {
    try {
      setNotification({
        show: true,
        type: 'info',
        title: 'Syncing Vault',
        message: 'Requesting master worker to sync data to vault...'
      });
      const res = await apiService.triggerVaultSync('upload');
      if (res.success) {
        setNotification({
          show: true,
          type: 'success',
          title: 'Sync Initiated',
          message: 'Data is being backed up to your Telegram Vault.'
        });
      } else {
        throw new Error(res.error || 'Unknown error');
      }
    } catch (err: any) {
      setNotification({
        show: true,
        type: 'error',
        title: 'Sync Failed',
        message: err.message
      });
    }
  };

  const handleRestoreVault = async () => {
    try {
      setNotification({
        show: true,
        type: 'info',
        title: 'Restoring Vault',
        message: 'Requesting master worker to download and distribute vault data...'
      });
      const res = await apiService.triggerVaultSync('download');
      if (res.success) {
        setNotification({
          show: true,
          type: 'success',
          title: 'Restore Initiated',
          message: 'Data is being restored from Telegram.'
        });
      } else {
        throw new Error(res.error || 'Unknown error');
      }
    } catch (err: any) {
      setNotification({
        show: true,
        type: 'error',
        title: 'Restore Failed',
        message: err.message
      });
    }
  };

  const handleAdd = () => {
    setSelectedAccount(null);
    setIsFormModalOpen(true);
  };

  const handleEdit = (account: SeedingAccount) => {
    setSelectedAccount(account);
    setIsFormModalOpen(true);
  };

  const handleDelete = (account: SeedingAccount) => {
    setSelectedAccount(account);
    setIsDeleteDialogOpen(true);
  };

  const handleViewDemographics = (account: SeedingAccount) => {
    setViewingAccount(account);
    setIsDemographicsModalOpen(true);
  };

  const handleDemographicsUpdate = () => {
    loadSeedingAccounts(); // Reload to get updated demographics
  };

  const confirmDelete = async () => {
    if (!selectedAccount) return;
    
    try {
      await deleteSeedingAccount(selectedAccount.id!);
      setIsDeleteDialogOpen(false);
      setSelectedAccount(null);
      setNotification({
        show: true,
        type: 'success',
        title: t('accountDeleted'),
        message: `${selectedAccount.username || selectedAccount.email}`
      });
    } catch (error: any) {
      setIsDeleteDialogOpen(false);
      
      // Handle 409 Conflict - account is being used in workflows
      // Note: axios interceptor in client.ts wraps errors and sets .status + .code
      // so we check both error.response?.status (raw axios) and error.status (wrapped)
      const is409 = error.response?.status === 409 || error.status === 409 || error.code === 'ACCOUNT_IN_USE';
      if (is409) {
        const errorData = error.response?.data || {};
        const workflowNames = errorData.workflows?.names?.join(', ') || error.details || 'some workflows';
        const workflowCount = errorData.workflows?.count || 'some';
        setNotification({
          show: true,
          type: 'warning',
          title: t('cannotDeleteTitle'),
          message: t('cannotDeleteInUse', { count: workflowCount }) + `: ${workflowNames}`
        });
      } else {
        // Other errors
        setNotification({
          show: true,
          type: 'error',
          title: t('deleteFailed'),
          message: error.response?.data?.error || error.message || t('deleteFailed')
        });
      }
    }
  };

  // ── TikTok QR login handlers ────────────────────────────────────────────
  const stopQrPolling = useCallback(() => {
    if (qrPollRef.current) { clearInterval(qrPollRef.current); qrPollRef.current = null; }
    if (qrAccountPollRef.current) { clearInterval(qrAccountPollRef.current); qrAccountPollRef.current = null; }
    workerSessionRef.current = null;
  }, []);

  const handleOpenQrModal = useCallback((account: SeedingAccount) => {
    setQrModal({ open: true, account, qrBase64: null, status: 'loading', expiresAt: null });
    workerSessionRef.current = null;

    // Resolve worker URL + JWT once, then poll the worker directly
    const workerUuid = account.worker_uuid || activeWorkerId || '';
    const fetchQr = async () => {
      if (!account.id || !workerUuid) return;
      try {
        // Fetch worker URL + JWT on first call; reuse cached value afterwards
        if (!workerSessionRef.current) {
          const urlResp = await apiService.getWorkerScheduleURL(workerUuid);
          workerSessionRef.current = { url: urlResp.worker.url, jwt: urlResp.jwt_token || '' };
        }
        const { url, jwt } = workerSessionRef.current;
        const res = await fetch(`${url}/accounts/${account.id}/tiktok-qr`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        if (!res.ok) return; // 404 = QR not yet available; keep polling
        const data = await res.json();
        if (data?.qr_base64) {
          setQrModal(prev => ({
            ...prev,
            qrBase64: data.qr_base64,
            expiresAt: data.expires_at,
            status: 'ready',
          }));
        }
      } catch {
        // Silently retry on next interval
      }
    };
    fetchQr();
    qrPollRef.current = setInterval(fetchQr, 5000);

    // Poll account status every 10 s to detect login success
    qrAccountPollRef.current = setInterval(async () => {
      await loadSeedingAccounts(true);
      // Re-read from store after refresh
    }, 10000);
  }, [loadSeedingAccounts]);

  const handleCloseQrModal = useCallback(() => {
    stopQrPolling();
    setQrModal({ open: false, account: null, qrBase64: null, status: 'loading', expiresAt: null });
  }, [stopQrPolling]);

  // Auto-close QR modal when the account becomes active
  useEffect(() => {
    if (!qrModal.open || !qrModal.account) return;
    const updated = seedingAccounts.find(a => a.id === qrModal.account!.id);
    if (updated?.status === 'active') {
      stopQrPolling();
      setQrModal(prev => ({ ...prev, status: 'success' }));
    }
  }, [seedingAccounts, qrModal.open, qrModal.account, stopQrPolling]);

  const handleFormClose = () => {
    setIsFormModalOpen(false);
    setSelectedAccount(null);
    setFormLoading(false);
    setQuickFillData(undefined);
  };

  // Main content: Account cards
  const renderMainContent = () => {
    // Empty state
    if (filteredAccounts.length === 0) {
      const platformName = activePlatform === 'all' 
        ? t('noAccountsYet') 
        : `${PLATFORM_LABELS[activePlatform] || activePlatform} - ${t('noAccountsYet')}`;
      const emptyMessage = activeWorkerId 
        ? t('thisWorkerNoAccounts')
        : t('addSeedingAccountsDesc');

      return (
        <div className="h-full flex flex-col">
          {/* Header with Add button */}
          <div className="p-4 border-b border-tg-section-separator bg-tg-section-bg flex justify-between items-center">
            <h3 className="text-lg font-semibold text-tg-text">
              {PLATFORM_LABELS[activePlatform] || activePlatform}
            </h3>
            <Button icon={<Plus size={20} />} onClick={handleAdd}>
              {t('addAccount')}
            </Button>
          </div>

          {/* Empty state */}
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="text-center max-w-md">
              <Mail size={64} className="mx-auto mb-4 text-tg-hint" />
              <h3 className="text-xl font-semibold text-tg-text mb-2">
                {platformName}
              </h3>
              <p className="text-tg-hint mb-6">
                {emptyMessage}
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Account list
    return (
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-tg-section-separator bg-tg-section-bg flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-tg-text">
              {PLATFORM_LABELS[activePlatform] || activePlatform}
              {activeWorkerId && <span className="text-sm text-tg-hint ml-2">({t('common:filter')})</span>}
            </h3>
            <p className="text-sm text-tg-hint">
              {t('accountCount_other', { count: filteredAccounts.length })}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" icon={<RefreshCw size={20} />} onClick={handleSyncVault}>
              Sync Vault (Upload)
            </Button>
            <Button variant="warning" icon={<RefreshCw size={20} />} onClick={handleRestoreVault}>
              Restore Vault (Download)
            </Button>
            <Button icon={<Plus size={20} />} onClick={handleAdd}>
              {t('addAccount')}
            </Button>
          </div>
        </div>

        {/* Account Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            {filteredAccounts.map((account) => (
              <Card key={account.id} className="hover:shadow-lg transition-shadow">
                <div className="p-4">
                  {/* Header: Platform icon + Status */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-tg-link">
                        {PLATFORM_ICONS[account.platform?.toLowerCase() || 'facebook']}
                      </span>
                      <span className="font-medium text-tg-text">
                        {PLATFORM_LABELS[account.platform?.toLowerCase() || 'facebook']}
                      </span>
                    </div>
                    {getAccountBadge(account, workerStatusMap)}
                  </div>

                  {/* Username/Email */}
                  <div className="mb-3">
                    <div className="flex items-center gap-2 text-sm">
                      <Mail size={16} className="text-tg-hint" />
                      <span className="text-tg-text truncate">{account.username || account.email}</span>
                      {account.email && account.email !== account.username && (
                        <span className="text-xs text-tg-hint">({account.email})</span>
                      )}
                    </div>
                  </div>

                  {/* Full Name */}
                  <div className="mb-3">
                    <div className="flex items-center gap-2 text-sm">
                      <User size={16} className="text-tg-hint" />
                      <span className="text-tg-text truncate">{account?.full_name || t('unnamed')}</span>
                    </div>
                  </div>

                  {/* Demographics Status */}
                  <div className="mb-3 flex items-center gap-2 text-xs">
                    <User size={14} className="text-tg-hint" />
                    <div className="flex gap-1">
                      {account.seeding_profile ? (
                        <>
                          <Badge variant="success" className="text-xs">{t('seedingProfile')}</Badge>
                          <span className="text-tg-hint">
                            {account.seeding_profile.role?.role_name} • {account.seeding_profile.age}yo
                          </span>
                        </>
                      ) : (
                        <span className="text-tg-hint">{t('noDemographics')}</span>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 mb-3 text-xs text-tg-hint">
                    {account.last_active_at && (
                      <div className="flex items-center gap-1">
                        <Clock size={14} />
                        <span>Active: {new Date(account.last_active_at).toLocaleDateString('en-US')}</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 pt-3 border-t border-tg-section-separator">
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<Eye size={16} />}
                      onClick={() => handleViewDemographics(account)}
                      className="flex-1 min-w-0"
                    >
                      <span className="truncate">{t('demographics')}</span>
                    </Button>
                    {/* TikTok QR login button — shown when account is not yet active */}
                    {account.platform?.toLowerCase() === 'tiktok' && account.status !== 'active' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<QrCode size={16} />}
                        onClick={() => handleOpenQrModal(account)}
                        className="flex-shrink-0 text-tg-link"
                        title="Đăng nhập bằng QR TikTok"
                      >
                        QR
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<Edit size={16} />}
                      onClick={() => handleEdit(account)}
                      className="flex-shrink-0"
                    >
                      {t('edit')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<Trash2 size={16} />}
                      onClick={() => handleDelete(account)}
                      className="text-tg-error hover:text-tg-error flex-shrink-0"
                    >
                      {t('delete')}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="h-full bg-tg-bg flex flex-col">
        {/* Facebook sub-tab bar (only shown when activePlatform is facebook) */}
        {activePlatform === 'facebook' && (
          <div className="flex border-b border-tg-section-separator bg-tg-section-bg px-4 pt-2 gap-1">
            <button
              type="button"
              onClick={() => setFbTab('personal')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                fbTab === 'personal'
                  ? 'text-[#1877F2] border-b-2 border-[#1877F2] bg-white'
                  : 'text-tg-hint hover:text-tg-text'
              }`}
            >
              Tài khoản cá nhân
            </button>
            <button
              type="button"
              onClick={() => setFbTab('fanpage')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                fbTab === 'fanpage'
                  ? 'text-[#1877F2] border-b-2 border-[#1877F2] bg-white'
                  : 'text-tg-hint hover:text-tg-text'
              }`}
            >
              Fanpage
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {activePlatform === 'facebook' && fbTab === 'fanpage' ? (
            <FanpageManagement />
          ) : (
            renderMainContent()
          )}
        </div>
      </div>
      {/* Form Modal */}
      <Modal
        isOpen={isFormModalOpen}
        onClose={handleFormClose}
        title={selectedAccount ? t('editAccount') : t('addNewAccount')}
        closeOnBackdropClick={false}
        footer={
          <div className="flex justify-between items-center">
            {!selectedAccount && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowQuickFill(true)}
                icon={<ClipboardPaste className="w-4 h-4" />}
              >
                {t('quickFill')}
              </Button>
            )}
            {selectedAccount && <div />}
            <Button
              type="button"
              variant="primary"
              loading={formLoading}
              size="md"
              onClick={() => {
                const form = document.getElementById('seeding-account-form') as HTMLFormElement;
                if (form) {
                  form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                }
              }}
            >
              {selectedAccount ? t('editAccount') : t('addAccount')}
            </Button>
          </div>
        }
      >
        <SeedingAccountForm
          account={selectedAccount || undefined}
          onClose={handleFormClose}
          onSubmitSuccess={() => {
            loadSeedingAccounts();
            // Notify parent to refresh workers list
            if (onDataChange) {
              onDataChange();
            }
          }}
          loading={formLoading}
          setLoading={setFormLoading}
          quickFillData={quickFillData}
        />
      </Modal>

      {/* Quick Fill Modal */}
      <Modal
        isOpen={showQuickFill}
        onClose={() => { setShowQuickFill(false); setQuickFillInput(''); }}
        title={t('quickFill')}
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setShowQuickFill(false); setQuickFillInput(''); }}>
              {t('common:cancel')}
            </Button>
            <Button variant="primary" size="sm" onClick={handleQuickFillConfirm} disabled={!quickFillInput.trim()}>
              {t('common:confirm')}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Paste account info separated by <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">|</code>
          </p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-gray-500 mb-1">Format:</p>
            <code className="text-xs text-gray-700 font-mono">
              Username | Password | 2FA | Email | Phone | Recovery Email | Full Name
            </code>
          </div>
          <textarea
            value={quickFillInput}
            onChange={(e) => setQuickFillInput(e.target.value)}
            placeholder="e.g. john@fb.com|MyP@ss123|ABCDEF123456|john@gmail.com"
            className="w-full p-3 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            rows={3}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (quickFillInput.trim()) handleQuickFillConfirm();
              }
            }}
          />
          {quickFillInput.trim() && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-blue-600 mb-1">Preview:</p>
              <div className="space-y-0.5">
                {quickFillInput.split('|').map((part, i) => (
                  <div key={i} className="flex gap-2 text-xs">
                    <span className="text-gray-400 min-w-[100px]">
                      {['Username', 'Password', '2FA', 'Email', 'Phone', 'Recovery Email', 'Full Name'][i] || `Field ${i + 1}`}:
                    </span>
                    <span className="text-gray-700 font-medium truncate">
                      {i === 1 ? '••••••••' : part.trim() || '(empty)'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title={t('deleteAccount')}
        message={t('deleteAccountConfirm', { email: selectedAccount?.username || selectedAccount?.email })}
        confirmLabel={t('delete')}
        cancelLabel={t('common:cancel')}
      />

      {/* Demographics Modal */}
      <Modal
        isOpen={isDemographicsModalOpen}
        onClose={() => setIsDemographicsModalOpen(false)}
        title={`${t('demographics')} - ${viewingAccount?.username || viewingAccount?.email}`}
        size="lg"
        footer={null}
      >
        {viewingAccount && (
          <DemographicsPanel
            accountId={viewingAccount.id!}
            demographics={{
              seeding_profile: viewingAccount.seeding_profile
            }}
            onUpdate={handleDemographicsUpdate}
          />
        )}
      </Modal>

      {/* TikTok QR Login Modal */}
      <Modal
        isOpen={qrModal.open}
        onClose={handleCloseQrModal}
        title={`TikTok QR Login — ${qrModal.account?.username || ''}`}
        size="sm"
        footer={
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={handleCloseQrModal}>
              Đóng
            </Button>
          </div>
        }
      >
        <div className="flex flex-col items-center gap-4 py-2">
          {qrModal.status === 'success' ? (
            <div className="flex flex-col items-center gap-2 py-6">
              <CheckCircle size={56} className="text-green-500" />
              <p className="text-tg-text font-semibold text-lg">Đăng nhập thành công!</p>
              <p className="text-tg-hint text-sm text-center">
                Tài khoản TikTok đã được xác thực.
              </p>
            </div>
          ) : qrModal.status === 'loading' || !qrModal.qrBase64 ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <RefreshCw size={32} className="text-tg-hint animate-spin" />
              <p className="text-tg-hint text-sm">Đang tải mã QR từ worker…</p>
              <p className="text-xs text-tg-hint text-center">
                Worker đang mở TikTok và chụp ảnh QR code.
                <br />Vui lòng chờ vài giây.
              </p>
            </div>
          ) : (
            <>
              <div className="bg-white border-2 border-gray-200 rounded-xl p-3 shadow-sm">
                <img
                  src={`data:image/png;base64,${qrModal.qrBase64}`}
                  alt="TikTok QR Code"
                  className="w-48 h-48 object-contain"
                />
              </div>
              <div className="text-center space-y-1">
                <p className="text-tg-text text-sm font-medium">
                  Mở app TikTok → Hồ sơ → Quét QR để đăng nhập
                </p>
                <p className="text-tg-hint text-xs">
                  QR tự động làm mới mỗi 60 giây
                </p>
                {qrModal.expiresAt && (
                  <p className="text-xs text-amber-500">
                    Hết hạn lúc {new Date(qrModal.expiresAt * 1000).toLocaleTimeString('vi-VN')}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.show}
        onClose={() => setNotification({ ...notification, show: false })}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />
    </>
  );
};
